import { router } from "@inertiajs/react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { LeaderboardEmpty } from "@/components/leaderboard/leaderboard-empty"
import { LeaderboardPodium } from "@/components/leaderboard/leaderboard-podium"
import { LeaderboardListRow } from "@/components/leaderboard/leaderboard-row"
import { LeaderboardSelfPin } from "@/components/leaderboard/leaderboard-self-pin"
import { LeaderboardSkeleton } from "@/components/leaderboard/leaderboard-skeleton"
import { LeaderboardTabs } from "@/components/leaderboard/leaderboard-tabs"
import type {
  LeaderboardPeriod,
  LeaderboardProps,
  LeaderboardRow,
  LeaderboardSelfRank,
} from "@/types/leaderboard"

/**
 * OKS-51 — Shared composition of the leaderboard surface.
 *
 * Used by:
 *   - `Creator/Leaderboard.tsx` — passes `audience="creator"` and the
 *     creator-side route helper.
 *   - `Brand/Crew/Leaderboard.tsx` — passes `audience="admin"`. The
 *     self-pin branch never fires for admins (`self_rank: null` by
 *     construction from `BrandLeaderboardController`).
 *
 * # 5 states (UX Spec §3)
 *
 *   - Loading  — `<LeaderboardSkeleton>` over the rankings area while
 *                an Inertia partial reload is in flight.
 *   - Empty    — `empty_kind === "no_crew" | "no_xp_in_window"` →
 *                `<LeaderboardEmpty>`.
 *   - Error    — handled by Inertia's `router.on('error')` upstream
 *                via the shared error boundary; this component does
 *                NOT need an in-component error branch in V1.
 *   - Success  — podium (when ≥3) + list + footer count + self-pin
 *                (creator + outside top-50 only).
 *   - Edge — 1/2 ranked rows → no podium, flat list.
 */
interface LeaderboardViewProps extends LeaderboardProps {
  audience: "creator" | "admin"
  /** Route helper for the surface (creatorLeaderboardPath or crewLeaderboardPath). */
  routeHelper: (options: { period: LeaderboardPeriod }) => string
}

export function LeaderboardView({
  period,
  rankings,
  self_rank,
  brand_member_count,
  empty_kind,
  viewer_membership_id,
  audience,
  routeHelper,
}: LeaderboardViewProps) {
  const { t } = useTranslation()
  const partialReloading = usePartialReloadInFlight()

  const showPodium = rankings.length >= 3
  const listRows = showPodium ? rankings.slice(3) : rankings
  // OKS-51 — `viewer_membership_id` drives the in-list highlight branch
  // (PRD AC §7 + TRD §4.2). Non-null for creator viewers; the row that
  // matches gets the accent treatment + "(You)" suffix. When the viewer
  // is OUTSIDE top-50, no in-list match occurs (their row isn't in
  // `rankings`) — `self_rank` then carries the separate pin payload.
  const viewerInListMembershipId =
    audience === "creator" ? viewer_membership_id : null

  return (
    <div className="flex w-full flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">{t("leaderboard.title")}</h1>
      </header>

      <LeaderboardTabs period={period} routeHelper={routeHelper} />

      {partialReloading ? (
        <LeaderboardSkeleton />
      ) : empty_kind ? (
        <LeaderboardEmpty
          emptyKind={empty_kind}
          period={period}
          audience={audience}
          switchAllTimeUrl={routeHelper({ period: "all" })}
        />
      ) : (
        <SuccessBody
          rankings={rankings}
          showPodium={showPodium}
          listRows={listRows}
          selfRank={self_rank}
          audience={audience}
          period={period}
          brandMemberCount={brand_member_count}
          viewerInListMembershipId={viewerInListMembershipId}
        />
      )}
    </div>
  )
}

interface SuccessBodyProps {
  rankings: LeaderboardRow[]
  showPodium: boolean
  listRows: LeaderboardRow[]
  selfRank: LeaderboardSelfRank
  audience: "creator" | "admin"
  period: LeaderboardPeriod
  brandMemberCount: number
  viewerInListMembershipId: number | null
}

function SuccessBody({
  rankings,
  showPodium,
  listRows,
  selfRank,
  audience,
  period,
  brandMemberCount,
  viewerInListMembershipId,
}: SuccessBodyProps) {
  const { t } = useTranslation()
  const showFooter = brandMemberCount > rankings.length

  // Self-pin only on the creator surface, only when the viewer is
  // OUTSIDE the visible top-50. Inside top-50, the in-list row is
  // highlighted via `viewerInListMembershipId` instead (no separate
  // pin — avoids visual duplication per UX Spec §1).
  const showSelfPin =
    audience === "creator" && selfRank !== null && selfRank.rank > 50

  return (
    <div className="flex flex-col gap-3">
      {showSelfPin && selfRank && (
        <LeaderboardSelfPin
          selfRank={selfRank}
          period={period}
          brandMemberCount={brandMemberCount}
        />
      )}

      {showPodium && (
        <LeaderboardPodium
          podium={rankings.slice(0, 3)}
          viewerMembershipId={viewerInListMembershipId}
        />
      )}

      <ul
        className="flex flex-col gap-1"
        data-testid="leaderboard-list"
        aria-label={t("leaderboard.list.aria_label")}
      >
        {listRows.map((row) => (
          <LeaderboardListRow
            key={row.membership_id}
            row={row}
            viewerHighlight={row.membership_id === viewerInListMembershipId}
          />
        ))}
      </ul>

      {showFooter && (
        <p
          className="text-muted-foreground text-center text-xs"
          data-testid="leaderboard-footer"
        >
          {t("leaderboard.footer.count", {
            shown: rankings.length,
            total: brandMemberCount,
          })}
        </p>
      )}
    </div>
  )
}

/**
 * Subscribes to Inertia's router lifecycle to surface the partial-
 * reload skeleton (UX Spec §3 loading state). Only flips for visits
 * that hit this page's render — not unrelated visits.
 */
function usePartialReloadInFlight() {
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const remove = router.on("start", (event) => {
      // Only treat partial reloads (the tabs use `only:`) as "loading
      // the rankings area". Full visits route through the page-level
      // skeleton instead.
      const only = (event.detail.visit as { only?: string[] }).only
      if (only?.includes("rankings")) {
        setLoading(true)
      }
    })
    const removeFinish = router.on("finish", () => setLoading(false))

    return () => {
      remove()
      removeFinish()
    }
  }, [])

  return loading
}
